/**
 * Autonomy facade API client (J6, Wave 3).
 *
 * Thin fetch wrapper for the Builder Service autonomy endpoints
 * (`/api/projects/:pid/autonomy/v1/settings|gates`). Follows the
 * launch-readiness client conventions: Load Balancer base URL from
 * `VITE_BUILDER_API_URL` + Firebase Auth bearer token.
 *
 * Envelope handling is dual on purpose (both styles exist in this repo):
 * - `{ success: true, data }` — builderSurface success envelope
 * - `{ apiVersion, asOf, data, warnings }` — controller envelope
 * Errors may be `{ success: false, error|message, code }` or problem-style
 * `{ code, reason }` / RFC-7807 `{ title, detail, code, status }`. All are
 * normalized into `AutonomyApiError` carrying `{status, code?, reason?}` so
 * 422 validation codes (`tier_locked`, `pricing_ceiling`,
 * `g3_composite_oec_required`) surface to the hooks intact.
 */

import { getAuth } from 'firebase/auth';
import type { AutonomySettingsData, AutonomySettingsUpdate } from '../types/autonomy.types';
import type { AutonomyGatesData } from '../types/gates.types';

export class AutonomyApiError extends Error {
  status: number;
  code?: string;
  /** Human-readable server reason (problem-style `reason`/`detail`). */
  reason?: string;

  constructor(message: string, status: number, code?: string, reason?: string) {
    super(message);
    this.name = 'AutonomyApiError';
    this.status = status;
    this.code = code;
    this.reason = reason;
  }
}

function getApiBaseUrl(): string {
  const url = import.meta.env.VITE_BUILDER_API_URL;
  if (!url) {
    throw new AutonomyApiError('VITE_BUILDER_API_URL is not configured', 0, 'CONFIG_MISSING');
  }
  return url;
}

async function getAuthToken(): Promise<string> {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) {
    throw new AutonomyApiError('Not authenticated', 401, 'UNAUTHORIZED');
  }
  return user.getIdToken();
}

/** Shared fetch + dual envelope unwrap for the autonomy endpoints. */
async function request<T>(path: string, method: 'GET' | 'PUT', payload?: unknown): Promise<T> {
  const token = await getAuthToken();
  const baseUrl = getApiBaseUrl();

  const response = await fetch(`${baseUrl}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    ...(payload !== undefined ? { body: JSON.stringify(payload) } : {}),
  });

  const body = await response.json().catch(() => ({}));
  if (!response.ok || body?.success === false) {
    const reason =
      typeof body?.reason === 'string'
        ? body.reason
        : typeof body?.detail === 'string'
          ? body.detail
          : undefined;
    const message =
      reason ||
      body?.error ||
      body?.message ||
      body?.title ||
      `API error: ${response.status}`;
    throw new AutonomyApiError(
      message,
      response.status,
      typeof body?.code === 'string' ? body.code : undefined,
      reason,
    );
  }
  return (body?.data ?? body) as T;
}

export const autonomyClient = {
  /** Server-resolved lever tiers + kill switch + enablement checklist. */
  async getSettings(projectId: string): Promise<AutonomySettingsData> {
    return request<AutonomySettingsData>(
      `/api/projects/${encodeURIComponent(projectId)}/autonomy/v1/settings`,
      'GET',
    );
  },

  /**
   * Partial update — send only changed levers and/or killSwitch. Validation
   * failures throw `AutonomyApiError` with the backend 400/422 code + reason.
   */
  async putSettings(
    projectId: string,
    update: AutonomySettingsUpdate,
  ): Promise<AutonomySettingsData> {
    return request<AutonomySettingsData>(
      `/api/projects/${encodeURIComponent(projectId)}/autonomy/v1/settings`,
      'PUT',
      update,
    );
  },

  /** G1–G10 activation gates board + refusals + chips + rollback links. */
  async getGates(projectId: string): Promise<AutonomyGatesData> {
    return request<AutonomyGatesData>(
      `/api/projects/${encodeURIComponent(projectId)}/autonomy/v1/gates`,
      'GET',
    );
  },
};

export default autonomyClient;
